import { BRANDS, type Brand } from './brands.js';

/**
 * Every grant is an authentik group named `role-<brand>-<level>`. Group membership is the
 * only source of truth: there is no second list of who may do what to keep in step with it.
 *
 * `member` is a brand's basic grant — today an identity and the mailbox at its address, and
 * whatever else that brand comes to offer. `owner` administers that brand's people. Both are
 * scoped to one brand, so owning Talt grants nothing over Royal Aura.
 */
export const ROLE_LEVELS = ['member', 'owner'] as const;

export type RoleLevel = (typeof ROLE_LEVELS)[number];

export interface Role {
  readonly brand: Brand;
  readonly level: RoleLevel;
}

export function roleGroup(brand: Brand, level: RoleLevel): string {
  return `role-${brand}-${level}`;
}

/** Every group this platform owns. Anything else a person holds is not ours to write. */
export const ROLE_GROUPS: readonly string[] = BRANDS.flatMap((brand) =>
  ROLE_LEVELS.map((level) => roleGroup(brand, level)),
);

/** The brand's basic grant: an identity, its address and the mailbox that address opens. */
export const MEMBER_GROUP: Readonly<Record<Brand, string>> = Object.fromEntries(
  BRANDS.map((brand) => [brand, roleGroup(brand, 'member')]),
) as Record<Brand, string>;

/** Administers one brand's people. Held on that brand's identity, never on another's. */
export const OWNER_GROUP: Readonly<Record<Brand, string>> = Object.fromEntries(
  BRANDS.map((brand) => [brand, roleGroup(brand, 'owner')]),
) as Record<Brand, string>;

/**
 * authentik's own superuser group. Its members own every brand, because everything the
 * platform can do they can already do in authentik directly. Nothing here ever adds or
 * removes it.
 */
export const SUPERUSER_GROUP = 'authentik Admins';

/**
 * What holding a brand's member role gives you. Mail is the whole list today; each new
 * resource is one entry here and the routes that read it.
 */
export const RESOURCES = ['mail'] as const;

export type Resource = (typeof RESOURCES)[number];

/** Owners hold every resource of the brand they own, so nobody administers what they cannot see. */
export const ROLE_RESOURCES: Readonly<Record<RoleLevel, readonly Resource[]>> = {
  member: RESOURCES,
  owner: RESOURCES,
};

export function parseRoleGroup(name: string): Role | null {
  for (const brand of BRANDS) {
    for (const level of ROLE_LEVELS) {
      if (roleGroup(brand, level) === name) return { brand, level };
    }
  }
  return null;
}

const byBrandThenLevel = (a: Role, b: Role): number =>
  BRANDS.indexOf(a.brand) - BRANDS.indexOf(b.brand) ||
  ROLE_LEVELS.indexOf(a.level) - ROLE_LEVELS.indexOf(b.level);

/**
 * The roles these groups grant on their own. The superuser group contributes nothing,
 * because it is not one of ours: provisioning writes `role-<brand>-<level>` and must
 * plan the same membership for a superuser as for anyone else, or it would try to
 * grant every brand to whoever administers authentik.
 *
 * Use {@link effectiveRoles} to describe what an account can actually do.
 */
export function assignedRoles(groups: readonly string[]): readonly Role[] {
  return groups.flatMap((name) => parseRoleGroup(name) ?? []).sort(byBrandThenLevel);
}

/**
 * What these groups let an account do. A superuser is expanded to owner and member of
 * every brand: the group is what grants it, and a screen that showed them as holding
 * nothing would be describing an account that can do everything.
 *
 * Use {@link assignedRoles} to decide what to write back.
 */
export function effectiveRoles(groups: readonly string[]): readonly Role[] {
  if (groups.includes(SUPERUSER_GROUP)) {
    return BRANDS.flatMap((brand) => ROLE_LEVELS.map((level) => ({ brand, level })));
  }
  return assignedRoles(groups);
}

export function brandsAt(roles: readonly Role[], level: RoleLevel): readonly Brand[] {
  return BRANDS.filter((brand) => roles.some((r) => r.brand === brand && r.level === level));
}

export function ownsBrand(roles: readonly Role[], brand: Brand): boolean {
  return roles.some((r) => r.brand === brand && r.level === 'owner');
}

/** Resources this set of roles opens on one brand. Empty means no access to that brand at all. */
export function resourcesOn(roles: readonly Role[], brand: Brand): readonly Resource[] {
  const held = new Set(
    roles.filter((r) => r.brand === brand).flatMap((r) => ROLE_RESOURCES[r.level]),
  );
  return RESOURCES.filter((resource) => held.has(resource));
}
